import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { getKeyCleanupJob, getKeyCleanupPreview, startKeyCleanup } from '@/services/api/custom-targeting';
import type { KeyCleanupInput, KeyCleanupJob, KeyCleanupPreview, KeyCleanupSkipped } from '@/types/custom-targeting';
import { formatCount, formatPercent } from '@/lib/keyCapacity';
import {
    CLEANUP_DEFAULT_IDLE_DAYS,
    CLEANUP_DEFAULT_QUANTITY,
    CLEANUP_FALLBACK_MIN_IDLE_DAYS,
    CLEANUP_MAX_QUANTITY,
    CLEANUP_POLL_MS,
    JOB_STATUS_LABEL,
    apiErrorDetail,
    clampIdleDays,
    clampQuantity,
    cleanupRuleText,
    isJobFinished,
    jobProgress,
    oldestExamples,
} from '@/lib/keyCleanup';
import { formatDateTimeShort, parseApiDate } from '@/lib/apiDate';

interface KeyCleanupDialogProps {
    keyName: string;
    onClose: () => void;
    /** Chamado uma vez quando o job termina, para recarregar a capacidade com refresh. */
    onFinished: () => void;
}

/** Espera o usuário parar de digitar antes de refazer a prévia. */
const DEBOUNCE_MS = 500;

/**
 * Liberar espaço numa key: prévia → "Desativar" → confirmação → acompanhamento.
 *
 * O que vai no POST é sempre o `quantity`/`idle_days` que a prévia devolveu,
 * não o texto dos campos — assim o número da confirmação é o número enviado.
 */
export default function KeyCleanupDialog({ keyName, onClose, onFinished }: KeyCleanupDialogProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [quantidadeTexto, setQuantidadeTexto] = useState(String(CLEANUP_DEFAULT_QUANTITY));
    const [diasTexto, setDiasTexto] = useState(String(CLEANUP_DEFAULT_IDLE_DAYS));
    const [minIdleDays, setMinIdleDays] = useState(CLEANUP_FALLBACK_MIN_IDLE_DAYS);
    const [params, setParams] = useState<KeyCleanupInput>({
        quantity: CLEANUP_DEFAULT_QUANTITY,
        idle_days: CLEANUP_DEFAULT_IDLE_DAYS,
    });
    const [confirmando, setConfirmando] = useState(false);
    const [jobId, setJobId] = useState<string | null>(null);

    useEffect(() => {
        const t = setTimeout(() => {
            setParams({ quantity: clampQuantity(quantidadeTexto), idle_days: clampIdleDays(diasTexto, minIdleDays) });
        }, DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [quantidadeTexto, diasTexto, minIdleDays]);

    const previewQuery = useQuery({
        queryKey: ['key-cleanup-preview', keyName, params.quantity, params.idle_days],
        queryFn: () => getKeyCleanupPreview(keyName, params),
        enabled: !jobId,
        retry: false,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
    });
    const preview = previewQuery.data;

    // A prévia traz o piso real de dias e, se houver, a limpeza que já está rodando.
    useEffect(() => {
        if (!preview) return;
        setMinIdleDays(preview.min_idle_days);
        if (preview.running_job) {
            queryClient.setQueryData(['key-cleanup-job', preview.running_job.id], preview.running_job);
            setJobId(preview.running_job.id);
        }
    }, [preview, queryClient]);

    const jobQuery = useQuery({
        queryKey: ['key-cleanup-job', jobId],
        queryFn: () => getKeyCleanupJob(jobId as string),
        enabled: !!jobId,
        retry: 1,
        refetchInterval: (query) => (isJobFinished(query.state.data) ? false : CLEANUP_POLL_MS),
        refetchOnWindowFocus: false,
    });
    const job = jobQuery.data;

    const avisouFim = useRef(false);
    useEffect(() => {
        if (job && isJobFinished(job) && !avisouFim.current) {
            avisouFim.current = true;
            onFinished();
        }
    }, [job, onFinished]);

    const iniciar = useMutation({
        mutationFn: (input: KeyCleanupInput) => startKeyCleanup(keyName, input),
        onSuccess: (novo) => {
            queryClient.setQueryData(['key-cleanup-job', novo.id], novo);
            avisouFim.current = false;
            setConfirmando(false);
            setJobId(novo.id);
        },
        onError: (error) => {
            setConfirmando(false);
            toast({ variant: 'destructive', title: 'Limpeza recusada', description: apiErrorDetail(error) });
        },
    });

    // A prévia na tela corresponde exatamente ao que está nos campos?
    const digitando =
        clampQuantity(quantidadeTexto) !== params.quantity || clampIdleDays(diasTexto, minIdleDays) !== params.idle_days;
    const previaValida = !!preview && !previewQuery.isFetching && !previewQuery.isError && !digitando;
    const podeDesativar = previaValida && !!preview && preview.max_to_deactivate > 0 && !preview.running_job;

    const novaPrevia = () => {
        setJobId(null);
        avisouFim.current = false;
        queryClient.removeQueries({ queryKey: ['key-cleanup-preview', keyName] });
    };

    return (
        <Dialog open onOpenChange={(aberto) => !aberto && !iniciar.isPending && onClose()}>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>
                        Liberar espaço em <span className="font-mono">{keyName}</span>
                    </DialogTitle>
                    <DialogDescription>
                        Desativa no GAM values antigos e sem tráfego, para abrir vaga no limite de ativos da key.
                    </DialogDescription>
                </DialogHeader>

                {jobId ? (
                    <Acompanhamento job={job} carregando={jobQuery.isLoading} erro={jobQuery.isError ? apiErrorDetail(jobQuery.error) : null} />
                ) : (
                    <>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="space-y-1.5">
                                <Label htmlFor="cleanup-quantity">Quantidade máxima</Label>
                                <Input
                                    id="cleanup-quantity"
                                    type="number"
                                    inputMode="numeric"
                                    min={1}
                                    max={CLEANUP_MAX_QUANTITY}
                                    step={1000}
                                    value={quantidadeTexto}
                                    onChange={(e) => setQuantidadeTexto(e.target.value)}
                                    onBlur={() => setQuantidadeTexto(String(clampQuantity(quantidadeTexto)))}
                                    // Travado na confirmação: o número confirmado é o que vai no POST.
                                    disabled={iniciar.isPending || confirmando}
                                />
                                <p className="text-[11px] text-slate-500">Até {formatCount(CLEANUP_MAX_QUANTITY)} por vez.</p>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="cleanup-idle">Dias sem tráfego</Label>
                                <Input
                                    id="cleanup-idle"
                                    type="number"
                                    inputMode="numeric"
                                    min={minIdleDays}
                                    step={1}
                                    value={diasTexto}
                                    onChange={(e) => setDiasTexto(e.target.value)}
                                    onBlur={() => setDiasTexto(String(clampIdleDays(diasTexto, minIdleDays)))}
                                    disabled={iniciar.isPending || confirmando}
                                />
                                <p className="text-[11px] text-slate-500">Mínimo {minIdleDays}.</p>
                            </div>
                        </div>

                        {previewQuery.isError ? (
                            <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-700 ring-1 ring-inset ring-rose-600/20">
                                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                <span>{apiErrorDetail(previewQuery.error, 'Não foi possível montar a prévia.')}</span>
                            </div>
                        ) : !preview ? (
                            <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
                                <Loader2 className="h-4 w-4 animate-spin" /> Montando a prévia…
                            </div>
                        ) : (
                            <Previa preview={preview} atualizando={previewQuery.isFetching || digitando} />
                        )}
                    </>
                )}

                {!jobId && confirmando && preview && (
                    <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-800 ring-1 ring-inset ring-rose-600/20">
                        <p className="font-semibold">
                            Tem certeza? Desativar até {formatCount(preview.max_to_deactivate)} values de{' '}
                            <span className="font-mono">{keyName}</span> no GAM.
                        </p>
                        <p className="mt-1 text-xs">
                            Regra: sem tráfego há {preview.idle_days} dias. Values desativados que voltarem a receber
                            tráfego são reativados automaticamente.
                        </p>
                    </div>
                )}

                <DialogFooter className="gap-2 sm:gap-2">
                    {jobId ? (
                        <>
                            {isJobFinished(job) && (
                                <Button variant="outline" size="sm" onClick={novaPrevia}>
                                    Nova prévia
                                </Button>
                            )}
                            <Button size="sm" onClick={onClose}>
                                Fechar
                            </Button>
                        </>
                    ) : confirmando ? (
                        <>
                            <Button variant="outline" size="sm" onClick={() => setConfirmando(false)} disabled={iniciar.isPending}>
                                Voltar
                            </Button>
                            <Button
                                size="sm"
                                className="bg-red-600 text-white hover:bg-red-700"
                                disabled={!podeDesativar || iniciar.isPending}
                                onClick={() => preview && iniciar.mutate({ quantity: preview.quantity, idle_days: preview.idle_days })}
                            >
                                {iniciar.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                                Sim, desativar
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" size="sm" onClick={onClose}>
                                Cancelar
                            </Button>
                            <Button
                                size="sm"
                                className="bg-red-600 text-white hover:bg-red-700"
                                disabled={!podeDesativar}
                                onClick={() => setConfirmando(true)}
                            >
                                Desativar
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Previa({ preview, atualizando }: { preview: KeyCleanupPreview; atualizando: boolean }) {
    const { regra, relatorio, soPorIdade } = cleanupRuleText(preview);
    const exemplos = oldestExamples(preview.examples);

    return (
        <div className={`space-y-4 transition-opacity ${atualizando ? 'opacity-50' : ''}`}>
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Numero rotulo="Ativos agora" valor={formatCount(preview.active)} detalhe={formatPercent(preview.percent)} />
                <Numero rotulo="Desativa até" valor={formatCount(preview.max_to_deactivate)} detalhe="values" />
                <Numero rotulo="Ativos depois, no máximo" valor={formatCount(preview.active_after_at_most)} detalhe="se todos saírem" />
            </dl>

            <div className={`rounded-lg p-3 text-sm ring-1 ring-inset ${soPorIdade ? 'bg-amber-50 text-amber-800 ring-amber-600/20' : 'bg-slate-50 text-slate-700 ring-slate-500/10'}`}>
                <p className="font-medium">Regra: {regra}</p>
                <p className="mt-1 text-xs">{relatorio}</p>
                <p className="mt-1 text-xs opacity-80">
                    Numa amostra de {formatCount(preview.sample_size)} candidatos, ficaram de fora: <Pulados s={preview.sample_skipped} />.
                    {preview.seen_in_report > 0 && ` ${formatCount(preview.seen_in_report)} apareceram no relatório.`}
                </p>
            </div>

            <div className="flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-800 ring-1 ring-inset ring-blue-600/20">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                <span>Values desativados que voltarem a receber tráfego são reativados automaticamente.</span>
            </div>

            <div>
                <p className="mb-1.5 text-xs font-medium text-slate-500">Os mais antigos que sairiam</p>
                {exemplos.length === 0 ? (
                    <p className="text-xs text-slate-400">Nenhum exemplo na amostra.</p>
                ) : (
                    <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-xs">
                        {exemplos.map((ex) => (
                            <li key={ex.gam_value_id ?? ex.name} className="flex items-center justify-between gap-3 px-3 py-1.5">
                                <span className="truncate font-mono text-slate-700" title={ex.name}>{ex.name}</span>
                                <span className="flex-shrink-0 tabular-nums text-slate-400">
                                    {formatDateTimeShort(parseApiDate(ex.processed_at))}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function Acompanhamento({ job, carregando, erro }: { job?: KeyCleanupJob; carregando: boolean; erro: string | null }) {
    if (!job) {
        return erro ? (
            <p className="text-sm text-rose-700">{erro}</p>
        ) : (
            <div className="flex items-center gap-2 py-8 text-sm text-slate-500">
                {carregando && <Loader2 className="h-4 w-4 animate-spin" />} Buscando a limpeza…
            </div>
        );
    }

    const rodando = job.status === 'running';
    const cor =
        job.status === 'done' ? 'bg-emerald-500' : job.status === 'running' ? 'bg-blue-500' : 'bg-rose-500';
    const inicio = parseApiDate(job.started_at);
    const fim = parseApiDate(job.finished_at);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 text-sm">
                <span className="inline-flex items-center gap-2 font-medium text-slate-800">
                    {rodando && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
                    {JOB_STATUS_LABEL[job.status]} — <span className="font-mono">{job.key_name}</span>
                </span>
                <span className="text-xs text-slate-500">
                    {inicio && `início ${formatDateTimeShort(inicio)}`}
                    {fim && ` · fim ${formatDateTimeShort(fim)}`}
                </span>
            </div>

            <div>
                <div className="mb-1 flex items-baseline justify-between text-sm tabular-nums">
                    <span className="font-semibold text-slate-900">
                        {formatCount(job.deactivated)} / {formatCount(job.quantity)} desativados
                    </span>
                    <span className="text-xs text-slate-500">{Math.round(jobProgress(job))}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100">
                    <div className={`h-full rounded-full transition-all ${cor}`} style={{ width: `${jobProgress(job)}%` }} />
                </div>
            </div>

            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Numero rotulo="Varridos" valor={formatCount(job.scanned)} />
                <Numero rotulo="Marcados na base" valor={formatCount(job.marked_in_base)} />
                <Numero rotulo="Sem tráfego há" valor={`${job.idle_days} dias`} />
            </dl>

            <p className="text-xs text-slate-500">
                Pulados: <Pulados s={job.skipped} />.
                {job.requested_by && ` Pedido por ${job.requested_by}.`}
            </p>

            {job.error && (
                <div className="flex items-start gap-2 rounded-lg bg-rose-50 p-3 text-sm text-rose-700 ring-1 ring-inset ring-rose-600/20">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{job.error}</span>
                </div>
            )}

            {rodando && (
                <p className="text-xs text-slate-400">
                    Pode fechar: a limpeza continua no servidor. Ao reabrir, a tela volta para este acompanhamento.
                </p>
            )}
        </div>
    );
}

function Pulados({ s }: { s: KeyCleanupSkipped }) {
    return (
        <span className="tabular-nums">
            {formatCount(s.trafego ?? 0)} com tráfego, {formatCount(s.recente)} recentes, {formatCount(s.na_fila)} na fila
        </span>
    );
}

function Numero({ rotulo, valor, detalhe }: { rotulo: string; valor: string; detalhe?: string }) {
    return (
        <div className="rounded-lg border border-slate-200 px-3 py-2">
            <dt className="text-[11px] text-slate-500">{rotulo}</dt>
            <dd className="text-lg font-semibold text-slate-900 tabular-nums">
                {valor}
                {detalhe && <span className="ml-1.5 text-xs font-normal text-slate-500">{detalhe}</span>}
            </dd>
        </div>
    );
}
